<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import CoLivingTabs from './CoLivingTabs.vue';
import SettingsCard from './components/SettingsCard.vue';
import HolidayModal from './components/HolidayModal.vue';
import { apiErrorMessage } from '../../../api/coLiving/errors';
import { CARD_CLASS, FIELD_CLASS } from './constants';
import {
  WEEKDAYS,
  normalizeWorkingHours,
  validateWorkingHours,
  workingHoursPayload,
} from './helpers';

export default {
  name: 'CoLivingCalendarPage',
  components: {
    PageHeader,
    LoadingState,
    DeleteModal,
    CoLivingTabs,
    SettingsCard,
    HolidayModal,
  },
  data() {
    return {
      cardClass: CARD_CLASS,
      fieldClass: FIELD_CLASS,
      weekdays: WEEKDAYS,
      hours: normalizeWorkingHours([]),
      hourErrors: {},
      isSavingHours: false,
      loadError: '',
      isHolidayModalOpen: false,
      editingHoliday: null,
      holidayToDelete: null,
      isDeleting: false,
    };
  },
  computed: {
    ...mapGetters('coLivingConfig', {
      storedHours: 'getWorkingHours',
      holidays: 'getHolidays',
      uiFlags: 'getUiFlags',
    }),
  },
  watch: {
    storedHours: {
      immediate: true,
      handler(rows) {
        this.hours = normalizeWorkingHours(rows);
      },
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    ...mapActions('coLivingConfig', [
      'fetchCalendar',
      'fetchSettings',
      'saveWorkingHours',
      'removeHoliday',
    ]),
    async load() {
      this.loadError = '';
      try {
        await Promise.all([this.fetchCalendar(), this.fetchSettings()]);
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.COMMON.LOAD_FAILED'
        );
      }
    },
    async onSaveHours() {
      this.hourErrors = validateWorkingHours(this.hours);
      if (Object.keys(this.hourErrors).length) return;
      this.isSavingHours = true;
      try {
        await this.saveWorkingHours(workingHoursPayload(this.hours));
        useAlert(this.$t('CO_LIVING.CALENDAR.HOURS_SAVED'));
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.CALENDAR.HOURS_FAILED'));
      } finally {
        this.isSavingHours = false;
      }
    },
    openAddHoliday() {
      this.editingHoliday = null;
      this.isHolidayModalOpen = true;
    },
    openEditHoliday(holiday) {
      this.editingHoliday = holiday;
      this.isHolidayModalOpen = true;
    },
    async confirmDelete() {
      this.isDeleting = true;
      try {
        await this.removeHoliday(this.holidayToDelete.id);
        useAlert(this.$t('CO_LIVING.CALENDAR.HOLIDAY_DELETED'));
        this.holidayToDelete = null;
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'CO_LIVING.CALENDAR.HOLIDAY_DELETE_FAILED')
        );
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto p-6" data-testid="co-living-calendar-page">
    <PageHeader
      :title="$t('CO_LIVING.TITLE')"
      icon="condo"
      :description="$t('CO_LIVING.DESCRIPTION')"
    />
    <CoLivingTabs />

    <LoadingState
      v-if="uiFlags.isFetchingCalendar && !storedHours.length"
      :message="$t('CO_LIVING.COMMON.LOADING')"
    />
    <p
      v-else-if="loadError"
      class="text-sm text-red-500"
      data-testid="co-living-load-error"
    >
      {{ loadError }}
    </p>

    <div v-else class="flex flex-col gap-6">
      <div>
        <h2 class="text-lg font-medium text-slate-900 dark:text-slate-100">
          {{ $t('CO_LIVING.CALENDAR.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
          {{ $t('CO_LIVING.CALENDAR.DESCRIPTION') }}
        </p>
      </div>

      <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <section :class="cardClass" data-testid="co-living-working-hours">
          <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
            {{ $t('CO_LIVING.CALENDAR.HOURS_TITLE') }}
          </h3>
          <p class="mt-1 mb-4 text-sm text-slate-600 dark:text-slate-300">
            {{ $t('CO_LIVING.CALENDAR.HOURS_DESCRIPTION') }}
          </p>

          <form class="flex flex-col gap-3" @submit.prevent="onSaveHours">
            <div v-for="row in hours" :key="row.weekday">
              <div class="flex items-center gap-3">
                <span
                  class="w-24 text-sm font-medium text-slate-900 dark:text-slate-100"
                >
                  {{ $t(`CO_LIVING.WEEKDAYS.${row.weekday}`) }}
                </span>
                <label
                  class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
                >
                  <input
                    v-model="row.is_open"
                    type="checkbox"
                    class="mb-0"
                    :data-testid="`co-living-hours-open-${row.weekday}`"
                  />
                  {{
                    row.is_open
                      ? $t('CO_LIVING.CALENDAR.OPEN')
                      : $t('CO_LIVING.CALENDAR.CLOSED')
                  }}
                </label>
                <template v-if="row.is_open">
                  <input
                    v-model="row.start"
                    type="time"
                    class="!w-32"
                    :class="[fieldClass]"
                    :data-testid="`co-living-hours-start-${row.weekday}`"
                  />
                  <span class="text-sm text-slate-500">
                    {{ $t('CO_LIVING.CALENDAR.TO') }}
                  </span>
                  <input
                    v-model="row.end"
                    type="time"
                    class="!w-32"
                    :class="[fieldClass]"
                    :data-testid="`co-living-hours-end-${row.weekday}`"
                  />
                </template>
              </div>
              <p
                v-if="hourErrors[row.weekday]"
                class="mt-1 text-xs text-red-500"
                :data-testid="`co-living-hours-error-${row.weekday}`"
              >
                {{
                  $t(
                    `CO_LIVING.CALENDAR.HOURS_ERRORS.${hourErrors[row.weekday]}`
                  )
                }}
              </p>
            </div>

            <div>
              <woot-button
                type="submit"
                :is-loading="isSavingHours"
                data-testid="co-living-save-hours"
              >
                {{ $t('CO_LIVING.CALENDAR.SAVE_HOURS') }}
              </woot-button>
            </div>
          </form>
        </section>

        <SettingsCard card-id="booking" />
      </div>

      <section :class="cardClass" data-testid="co-living-holidays">
        <div class="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
              {{ $t('CO_LIVING.CALENDAR.HOLIDAYS_TITLE') }}
            </h3>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {{ $t('CO_LIVING.CALENDAR.HOLIDAYS_DESCRIPTION') }}
            </p>
          </div>
          <woot-button
            icon="add"
            data-testid="co-living-add-holiday"
            @click="openAddHoliday"
          >
            {{ $t('CO_LIVING.CALENDAR.ADD_HOLIDAY') }}
          </woot-button>
        </div>

        <p
          v-if="!holidays.length"
          class="text-sm text-slate-500 dark:text-slate-400"
          data-testid="co-living-holidays-empty"
        >
          {{ $t('CO_LIVING.CALENDAR.HOLIDAYS_EMPTY') }}
        </p>
        <table
          v-else
          class="w-full text-sm"
          data-testid="co-living-holiday-table"
        >
          <thead>
            <tr class="text-left text-slate-500 dark:text-slate-400">
              <th class="p-2">
                {{ $t('CO_LIVING.CALENDAR.HOLIDAY_COLUMNS.DATE') }}
              </th>
              <th class="p-2">
                {{ $t('CO_LIVING.CALENDAR.HOLIDAY_COLUMNS.NAME') }}
              </th>
              <th class="p-2">
                {{ $t('CO_LIVING.CALENDAR.HOLIDAY_COLUMNS.SCOPE') }}
              </th>
              <th class="p-2" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="holiday in holidays"
              :key="holiday.id"
              class="border-t border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              :data-testid="`co-living-holiday-row-${holiday.id}`"
            >
              <td class="p-2">{{ holiday.date }}</td>
              <td class="p-2">{{ holiday.name }}</td>
              <td class="p-2">
                {{ holiday.site_name || $t('CO_LIVING.CALENDAR.ALL_CONDOS') }}
              </td>
              <td class="p-2 text-right whitespace-nowrap">
                <woot-button
                  v-tooltip.top="$t('CO_LIVING.COMMON.EDIT')"
                  variant="clear"
                  size="small"
                  icon="edit"
                  :data-testid="`co-living-holiday-edit-${holiday.id}`"
                  :aria-label="$t('CO_LIVING.COMMON.EDIT')"
                  @click="openEditHoliday(holiday)"
                />
                <woot-button
                  v-tooltip.top="$t('CO_LIVING.COMMON.DELETE')"
                  variant="clear"
                  size="small"
                  color-scheme="alert"
                  icon="delete"
                  :data-testid="`co-living-holiday-delete-${holiday.id}`"
                  :aria-label="$t('CO_LIVING.COMMON.DELETE')"
                  @click="holidayToDelete = holiday"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>

    <HolidayModal
      v-if="isHolidayModalOpen"
      :holiday="editingHoliday"
      @close="isHolidayModalOpen = false"
    />
    <DeleteModal
      v-if="holidayToDelete"
      :delete-name="holidayToDelete.name"
      :is-loading="isDeleting"
      @close="holidayToDelete = null"
      @confirm="confirmDelete"
    />
  </div>
</template>
